import React from 'react';
import styled from '@emotion/styled';
import { text } from 'style/mixin';
import fonts from 'style/fonts';
import { mq } from 'style/variables';
import { CareerDetailHeading } from 'components/unique/careerDetail/careerDetailHeading';
import scopeImage from 'assets/images/career/scope.png';
import ScrollGrayCover from 'components/atoms/img/ScrollGrayCover';

type CareerDetailScopeProps = {
  desc1: string;
  desc2: string;
  outputsLabel: string;
  outputsImage: Array<{
    url: string;
    desc: string;
    url_org: string;
  }>;
  scopeImage?: {
    url?: string | null;
    desc?: string | null;
    url_org?: string | null;
  };
};

const ScopeWrapper = styled.div``;

const ScopeImageWrapper = styled.div`
  margin-top: 34px;

  ${mq.onlysp} {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: calc(100% + 32px);
    margin-right: -32px;

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.3) transparent;

    /* WebKit */
    &::-webkit-scrollbar {
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.3);
      border-radius: 999px;
    }
  }
`;

const ScopeImage = styled.img`
  width: 100%;
  height: auto;

  ${mq.onlysp} {
    width: auto;
    height: auto;
    max-width: none;
    max-height: 400px;
  }
`;

const ScopeContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 58px;
  margin: 46px auto 0;
  padding: 0 38px;

  ${mq.onlysp} {
    grid-template-columns: 1fr;
    gap: 28px;
    padding: 0;
    margin: 46px auto 0;
  }
`;

const ScopeContentColumn = styled.div`
  ${text(18, 1.78)}
  font-weight: 500;
  font-family: ${fonts.jaGothic};
  letter-spacing: 0.25rem;
  text-align: justify;
  white-space: pre-wrap;

  ${mq.onlysp} {
    ${text(16, 1.82)}
  }
`;

const ScopeOutputWrapper = styled.div`
  margin-top: 58px;

  ${mq.onlysp} {
    margin-top: 48px;
  }
`;

const ScopeOutputTitle = styled.h3`
  ${text(22, 1)}
  font-weight: 600;
  font-family: ${fonts.jaGothic};
  letter-spacing: 0.15rem;
`;

const ScopeOutputContent = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 22px 20px;
  margin-top: 28px;

  ${mq.onlysp} {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px 12px;
    margin-top: 20px;
  }
`;

const ScopeOutputItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  ${mq.onlysp} {
    gap: 11px;
  }
`;

const ScopeOutputImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
`;

const ScopeOutputItemTitle = styled.h4`
  ${text(11, 1)}
  font-weight: 500;
  text-align: center;
`;

export const CareerDetailScope = ({
  desc1,
  desc2,
  outputsLabel,
  outputsImage,
  scopeImage: scopeImageProp,
}: CareerDetailScopeProps) => {
  return (
    <ScopeWrapper>
      <CareerDetailHeading titleEn="Scope" titleJa="スコープ" />
      <ScopeImageWrapper>
        <ScopeImage src={scopeImageProp?.url || scopeImage} alt={scopeImageProp?.desc || ''} />
      </ScopeImageWrapper>
      <ScopeContentWrapper>
        <ScopeContentColumn dangerouslySetInnerHTML={{ __html: desc1 }} />
        <ScopeContentColumn dangerouslySetInnerHTML={{ __html: desc2 }} />
      </ScopeContentWrapper>
      {outputsLabel && (
        <ScopeOutputWrapper>
          <ScopeOutputTitle>{outputsLabel}</ScopeOutputTitle>
          <ScopeOutputContent>
            {outputsImage.map((image, index) => (
              <ScopeOutputItem key={index}>
                <ScrollGrayCover>
                  <ScopeOutputImage src={image.url} />
                </ScrollGrayCover>
                <ScopeOutputItemTitle>{image.desc || ''}</ScopeOutputItemTitle>
              </ScopeOutputItem>
            ))}
          </ScopeOutputContent>
        </ScopeOutputWrapper>
      )}
    </ScopeWrapper>
  );
};
